import proj4 from 'proj4';
import { EASTERN_GALILEE_BOUNDS } from '@/lib/map/eastern-galilee';

/** Israeli Transverse Mercator — EPSG:2039 (New Israel Grid). */
export const ITM_PROJ4 =
  '+proj=tmerc +lat_0=31.7343936111111 +lon_0=35.2045169444444 +k=1.0000067 +x_0=219529.584 +y_0=626907.390 +ellps=GRS80 +units=m +no_defs';

proj4.defs('ITM', ITM_PROJ4);

export type ItmPoint = { easting: number; northing: number };

export type ItmValidationCode =
  | 'invalid_number'
  | 'utm_like'
  | 'ics_like'
  | 'out_of_bounds';

export const ITM_VALIDATION_MESSAGES: Record<ItmValidationCode, string> = {
  invalid_number: 'יש להזין מזרח וצפון מספריים',
  utm_like: 'נראה כמו קואורדינטות UTM — יש להזין נ.צ ברשת ישראל החדשה (ITM)',
  ics_like:
    'נראה כמו רשת ישראל ישנה — יש להזין נ.צ ברשת ישראל החדשה (ITM)',
  out_of_bounds: 'נ.צ מחוץ לתחום אשכול גליל מזרחי',
};

let cachedItmBounds: {
  minE: number;
  maxE: number;
  minN: number;
  maxN: number;
} | null = null;

function getEasternGalileeItmBounds() {
  if (cachedItmBounds) return cachedItmBounds;
  const sw = wgs84ToItm(EASTERN_GALILEE_BOUNDS.west, EASTERN_GALILEE_BOUNDS.south);
  const ne = wgs84ToItm(EASTERN_GALILEE_BOUNDS.east, EASTERN_GALILEE_BOUNDS.north);
  cachedItmBounds = {
    minE: Math.min(sw.easting, ne.easting),
    maxE: Math.max(sw.easting, ne.easting),
    minN: Math.min(sw.northing, ne.northing),
    maxN: Math.max(sw.northing, ne.northing),
  };
  return cachedItmBounds;
}

export function wgs84ToItm(lng: number, lat: number): ItmPoint {
  const [easting, northing] = proj4('EPSG:4326', 'ITM', [lng, lat]);
  return { easting, northing };
}

export function itmToWgs84(easting: number, northing: number): { lng: number; lat: number } {
  const [lng, lat] = proj4('ITM', 'EPSG:4326', [easting, northing]);
  return { lng, lat };
}

export function formatItm(easting: number, northing: number): ItmPoint {
  return { easting: Math.round(easting), northing: Math.round(northing) };
}

export function isWithinEasternGalileeItm(easting: number, northing: number): boolean {
  const b = getEasternGalileeItmBounds();
  return (
    easting >= b.minE &&
    easting <= b.maxE &&
    northing >= b.minN &&
    northing <= b.maxN
  );
}

export function isLikelyIcsGrid(easting: number, northing: number): boolean {
  return northing > 1_000_000 && easting < 250_000;
}

export function isLikelyUtmNorthing(northing: number): boolean {
  return northing > 3_000_000;
}

export function validateItmPair(
  easting: number,
  northing: number,
): ItmValidationCode | null {
  if (!Number.isFinite(easting) || !Number.isFinite(northing)) {
    return 'invalid_number';
  }
  if (isLikelyUtmNorthing(northing)) return 'utm_like';
  if (isLikelyIcsGrid(easting, northing)) return 'ics_like';
  if (!isWithinEasternGalileeItm(easting, northing)) return 'out_of_bounds';
  return null;
}

export function looksLikeWgs84Pair(coordX: number, coordY: number): boolean {
  return Math.abs(coordX) <= 180 && Math.abs(coordY) <= 90;
}

/** Re-derive ITM from lat/lng when draft stored WGS84 in coord fields. */
export function resolveItmFromForm(
  latStr: string,
  lngStr: string,
  coordXStr: string,
  coordYStr: string,
): ItmPoint | null {
  const lat = Number(latStr);
  const lng = Number(lngStr);
  const coordX = Number(coordXStr);
  const coordY = Number(coordYStr);

  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    if (
      !coordXStr.trim() ||
      !coordYStr.trim() ||
      looksLikeWgs84Pair(coordX, coordY)
    ) {
      const derived = wgs84ToItm(lng, lat);
      return formatItm(derived.easting, derived.northing);
    }
  }

  if (Number.isFinite(coordX) && Number.isFinite(coordY) && !looksLikeWgs84Pair(coordX, coordY)) {
    return formatItm(coordX, coordY);
  }

  return null;
}

export const GOLDEN_GALILEE_WGS84 = { lng: 35.63, lat: 32.84 } as const;
